import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { MovePreviewDto, MoveResultDto, ProfileDto } from '@tiffinify/contracts';
import { addDays, formatDay, mealsOf, type Meal } from '@tiffinify/core';
import { Card, Chip, Chips, ErrorText, MealIcon, Skel, TiffinMini, LoadError, ToggleRow } from '../components/ui';
import { Back, Btn, Card as PCard, Empty as PEmpty, KV, Screen, Title, Top } from '../components/p';
import type { Flash } from '../lib/flash';
import { ApiClientError, isNotFound, post } from '../lib/api';
import { errorMessage, mealName } from '../lib/format';
import { useMe, useMoveOptions, useProfile } from '../lib/queries';

type Ref = { day: string; meal: Meal };

export function Move() {
  const { id = '' } = useParams();
  const profile = useProfile(id);
  const p = profile.data;
  const sub = p?.subscription;

  const head = <Top><Back to={`/s/${id}`} label={p?.subscriber.name.split(' ')[0] ?? 'Back'} /></Top>;
  if (profile.isPending) return <Screen gap={18}>{head}<Title h="Move a meal" sub="No meal is lost." /><MoveSkeleton /></Screen>;
  if (profile.isError && !isNotFound(profile.error)) return <Screen gap={18}>{head}<LoadError what="this plan" onRetry={() => void profile.refetch()} /></Screen>;
  if (!p || !sub || !p.summary || p.summary.state === 'ended' || p.summary.state === 'closed') return <Screen gap={18}>{head}<PCard><PEmpty title="No active plan" /></PCard></Screen>;
  return <MoveForm p={p} />;
}

/** The two steps' chips and meal buttons, held in place while the options load. */
function MoveSkeleton() {
  const step = (label: string) => (
    <div className="stack">
      <span className="label">{label}</span>
      <Chips label={label} scroll>{[0, 1, 2, 3].map((i) => <Chip key={i} fixed><Skel w={64} /></Chip>)}</Chips>
      <div className="p-seg" aria-hidden="true">{[0, 1].map((i) => <button key={i} type="button" tabIndex={-1}><Skel w={64} /></button>)}</div>
    </div>
  );
  return <>{step('Which meal?')}{step('Deliver it on')}</>;
}

/** One button per meal; a meal with a reason it cannot be picked is disabled, with the reason as its title. */
function MealButtons({ label, meals, value, onChange, reasonFor }: {
  label: string; meals: Meal[]; value: Meal | undefined; onChange: (m: Meal) => void; reasonFor?: (m: Meal) => string | undefined;
}) {
  return (
    <div role="group" aria-label={label} className="p-seg">
      {meals.map((m) => {
        const reason = reasonFor?.(m);
        return (
          <button key={m} type="button" className={m === value ? 'on' : undefined} aria-pressed={m === value} disabled={!!reason} title={reason} onClick={() => onChange(m)}>
            <MealIcon meal={m} size={16} />
            {mealName(m)}
          </button>
        );
      })}
    </div>
  );
}

function MoveForm({ p }: { p: ProfileDto }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const sub = p.subscription!;
  const today = useMe().data?.today ?? '';
  const options = useMoveOptions(sub.id);
  const o = options.data;
  // What the user picked; null = the default (first meal that can move, delivered on the next day).
  const [fromDay, setFromDay] = useState<string | null>(null);
  const [fromMeal, setFromMeal] = useState<Meal | null>(null);
  const [toDay, setToDay] = useState<string | null>(null);
  const [toMeal, setToMeal] = useState<Meal | null>(null);
  const [confirm, setConfirm] = useState(true);

  const fromDays = o?.from ?? [];
  const served = o ? mealsOf(o.meals) : [];
  const fromOpt = fromDays.find((d) => d.day === fromDay) ?? fromDays[0];
  const from: Ref | undefined = fromOpt && { day: fromOpt.day, meal: fromMeal && fromOpt.meals.includes(fromMeal) ? fromMeal : fromOpt.meals[0]! };

  const dayLabel = (d: string) => (d === today ? 'Today' : today && d === addDays(today, 1) ? 'Tomorrow' : formatDay(d));

  /** Meals the plan already has on a day, not counting the one being moved away. */
  const onPlan = (day: string | undefined): Meal[] =>
    (day && o?.existing?.find((e) => e.day === day)?.meals.filter((m) => !(from && day === from.day && m === from.meal))) || [];
  /** Why a meal on a day cannot take the moved meal: it is that meal itself, or the plan already has it then. */
  const takenReason = (day: string | undefined, meal: Meal): string | undefined => {
    if (!day || !from) return undefined;
    if (day === from.day && meal === from.meal) return 'Pick a different day or meal to deliver it on';
    return onPlan(day).includes(meal) ? `Already on the plan for ${dayLabel(day)}` : undefined;
  };
  const freeMeals = (day: string | undefined) => (day ? served.filter((m) => !takenReason(day, m)) : []);

  // Default: the next delivery day with a free meal (the same meal when it can), else another meal that day, else any day.
  const dflt = ((): Ref | undefined => {
    if (!o || !from) return undefined;
    for (const d of [...o.to.filter((x) => x > from.day), ...o.to.filter((x) => x === from.day), ...o.to]) {
      const free = freeMeals(d);
      if (free.length) return { day: d, meal: free.includes(from.meal) ? from.meal : free[0]! };
    }
    return undefined;
  })();
  const toD = toDay && o?.to.includes(toDay) ? toDay : dflt?.day;
  const free = freeMeals(toD);
  const toM = toMeal && free.includes(toMeal) ? toMeal : from && free.includes(from.meal) ? from.meal : free[0];
  const to: Ref | undefined = toD && toM ? { day: toD, meal: toM } : undefined;
  const same = !!from && !!to && from.day === to.day && from.meal === to.meal;
  const body = { from, to };

  const preview = useQuery({
    queryKey: ['move-preview', sub.id, from, to],
    queryFn: () => post<MovePreviewDto>(`/subscriptions/${sub.id}/moves/preview`, body),
    enabled: !!from && !!to && !same,
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: () => post<MoveResultDto>(`/subscriptions/${sub.id}/moves`, body),
    onSuccess: (res) => {
      void qc.invalidateQueries();
      const ends = res.newEndDate !== res.currentEndDate ? ` The plan now ends ${formatDay(res.newEndDate)}.` : '';
      const flash: Flash = {
        text: `Meal moved. The ${from!.meal} on ${formatDay(from!.day)} now comes as ${to!.meal} on ${formatDay(to!.day)}.${ends}`,
        ...(confirm ? { wa: { label: `Send to ${p.subscriber.name.split(' ')[0]}`, link: res.message.link } } : {}),
      };
      nav(`/s/${p.subscriber.id}`, { state: { flash } });
    },
    // Someone else moved or delivered this meal meanwhile: refresh what can still move.
    onError: (e) => { if (e instanceof ApiClientError && e.status === 409) void qc.invalidateQueries({ queryKey: ['move-options', sub.id] }); },
  });

  const pickFromDay = (d: string) => { setFromDay(d); setFromMeal(null); setToMeal(null); save.reset(); };
  const first = p.subscriber.name.split(' ')[0] ?? p.subscriber.name;

  const head = (
    <>
      <Top><Back to={`/s/${p.subscriber.id}`} label={first} /></Top>
      <Title h="Move a meal" sub="No meal is lost." />
    </>
  );
  if (options.isPending) return <Screen gap={18}>{head}<MoveSkeleton /></Screen>;
  if (options.error || !o) return <Screen gap={18}>{head}<LoadError what="the meals" onRetry={() => void options.refetch()} /></Screen>;
  if (!from) return <Screen gap={18}>{head}<PCard><PEmpty title="No meals to move">Nothing left on the plan in the next few weeks can be moved.</PEmpty></PCard></Screen>;

  const pv = preview.data;
  const oldEnd = o.endDate;
  const newEnd = pv && !same && !preview.error ? pv.newEndDate : oldEnd;
  const endMoves = newEnd !== oldEnd;
  // Server checks show under the step they are about.
  const err = preview.error && !same ? errorMessage(preview.error) : '';
  const fromErr = preview.error instanceof ApiClientError && !!preview.error.fields.from;

  // What the new day holds after the move, as the cook will see it.
  const thatDay = to ? [...onPlan(to.day), to.meal].sort((x, y) => served.indexOf(x) - served.indexOf(y)) : [];

  return (
    <Screen gap={18}>
      {head}

      <div className="stack">
        <span className="label">Meal to move</span>
        <Chips label="Day of the meal" scroll>
          {fromDays.map((d) => <Chip key={d.day} on={d.day === from.day} onClick={() => pickFromDay(d.day)}>{dayLabel(d.day)}</Chip>)}
        </Chips>
        {fromOpt!.meals.length > 1 && <MealButtons label="Meal to move" meals={fromOpt!.meals} value={from.meal} onChange={(m) => { setFromMeal(m); setToMeal(null); save.reset(); }} />}
        <ErrorText>{fromErr ? err : ''}</ErrorText>
      </div>

      <div className="stack">
        <span className="label">Deliver it on</span>
        <Chips label="New day" scroll>
          {o.to.map((d) => <Chip key={d} on={d === to?.day} onClick={() => { setToDay(d); setToMeal(null); save.reset(); }}>{dayLabel(d)}</Chip>)}
        </Chips>
        <MealButtons label="New meal" meals={served} value={to?.meal} onChange={(m) => { setToMeal(m); save.reset(); }} reasonFor={(m) => takenReason(toD, m)} />
        {toD && (free.length === 0
          ? <span className="hint bad" role="status">Every meal is already on the plan for {dayLabel(toD)}. Pick another day.</span>
          : onPlan(toD).length > 0 && <span className="hint">{onPlan(toD).map(mealName).join(' and ')} {onPlan(toD).length > 1 ? 'are' : 'is'} already on the plan for {dayLabel(toD)}.</span>)}
        <ErrorText>{fromErr ? '' : err}</ErrorText>
      </div>

      <PCard pad style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <KV k="From" v={`${dayLabel(from.day)} · ${from.meal}`} />
        <KV k="To" v={to ? `${dayLabel(to.day)} · ${to.meal}` : '…'} />
        {to && !same && <KV k={dayLabel(to.day)} v={`${thatDay.map(mealName).join(' and ')} for ${first}`} />}
        <KV k="Plan ends" v={<span className="p-pair">{endMoves ? `${formatDay(newEnd)} (was ${formatDay(oldEnd)})` : `${formatDay(newEnd)}, unchanged`}{preview.isFetching && <span className="hero-busy" aria-label="Recalculating"><TiffinMini /></span>}</span>} />
      </PCard>
      {same && <span className="p-status">Pick a different day or meal to deliver it on.</span>}

      <Card className="toggle-card"><ToggleRow title={`Tell ${first} on WhatsApp`} sub="With the new day" on={confirm} onChange={setConfirm} /></Card>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <Btn
        busy={save.isPending}
        label={same || !to ? (free.length === 0 ? 'Pick another day' : 'Pick a new day') : `Move meal${confirm ? ` · tell ${first}` : ''}`}
        go={confirm ? 'send' : 'check'}
        onClick={() => save.mutate()}
        disabled={save.isPending || same || !to || !!preview.error}
      />
    </Screen>
  );
}
